import React, { useState } from 'react';
import { Copy, Check, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import toast from 'react-hot-toast';

interface WebhookURLDisplayProps {
  webhookUrl: string;
  onCheckSample: () => void;
}

const WebhookURLDisplay: React.FC<WebhookURLDisplayProps> = ({
  webhookUrl,
  onCheckSample
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      toast.success('Webhook URL copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-info/15 border border-info/20 rounded-lg p-4">
        <h3 className="font-semibold text-info mb-2">
          Step 2: Configure Your Webhook
        </h3>
        <p className="text-sm text-info">
          Copy the URL below and paste it into your external service's webhook settings. 
          Then send a test webhook to capture the payload structure.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Your Webhook URL
        </label>
        <div className="flex items-center space-x-2">
          <div className="flex-1 px-4 py-3 bg-muted border border-input rounded-md font-mono text-sm break-all">
            {webhookUrl}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            className="shrink-0"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="bg-warn/15 border border-warn/20 rounded-lg p-4">
        <h4 className="font-medium text-warn mb-2">
          📋 Instructions
        </h4>
        <ol className="list-decimal list-inside space-y-2 text-sm text-warn">
          <li>Go to your service's webhook configuration page</li>
          <li>Add the webhook URL above as a new endpoint</li>
          <li>Send a test webhook from your service</li>
          <li>Click "Check for Sample" below once the test is sent</li>
        </ol>
      </div>

      <div className="flex justify-end">
        <Button onClick={onCheckSample}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Check for Sample
        </Button>
      </div>
    </div>
  );
};

export default WebhookURLDisplay;

